import './util/polyfills';
import app from './view/app';
import { onContentLoaded } from './util/dom';
import { getScriptTag, parseURL } from './util/url';
import { evolve } from './util/object';
import BackendAPI from './api/backend';
import FrontendAPI from './api/frontend';
import FrontendModAPI from './api/frontend-mod';
import { fetchGVL } from './service/vendor-list';
import debug, { init as initDebug, ERROR, DEBUG, log } from './util/debug';
import { searchParams } from './util/location';
import getI18n from './i18n';
import { defaultBanner, videoBanner } from './view/banner';
import { translate } from './util/view';
import { getApplicableRegulations } from './util/regulations';
import { getGeoInfo } from './service/geoInfo';
const parseBoolAttribute = (x) => ['', 'true'].includes(x);
const parseJSONAttribute = (x) => JSON.parse(x);
const evolveOptions = evolve({
    render: parseBoolAttribute,
    renderVideo: parseBoolAttribute,
    renderConfig: parseJSONAttribute,
    shouldConsentManagementBeEnforced: parseBoolAttribute,
});
const getOptions = (doc = document) => {
    const cmpScript = getScriptTag(doc);
    const scriptVersionBaseUrl = cmpScript
        ? parseURL(cmpScript.src).href.replace(/\/iife\/mol-ads-cmp.*\.js$/, '')
        : '';
    const options = {
        api: 'frontend',
        backendBaseUrl: 'https://cmp.dmgmediaprivacy.co.uk',
        cvlUiVersion: 1,
        gvlUiVersion: 186,
        i18nVersion: 7,
        logLevel: searchParams['mol.ads.cmp.log.level'] || 'off',
        nrvUiVersion: 1,
        region: 'unknown',
        render: false,
        renderConfig: {
            layerTwo: {
                ccpaFrameworkText: 'California Consumer Privacy Act',
                footerPrivacyText: 'Do not sell my info',
            },
            layerThree: {
                privacyText: 'Do Not Sell My Info',
            },
        },
        scriptVersionBaseUrl,
        vendorListBaseUrl: 'https://cmp.dmgmediaprivacy.co.uk',
        infoSvg: 'whiteOnTransparent',
    };
    for (const option in cmpScript ? cmpScript.dataset : {}) {
        if (cmpScript.dataset.hasOwnProperty(option)) {
            Object.assign(options, evolveOptions({ [option]: cmpScript.dataset[option] }));
        }
    }
    return options;
};
const buildFrontendAPI = async ({ banner, backendBaseUrl, clientConsentString = '', cvlUiVersion, forceSetConsentString = false, geo, gvlUiVersion, i18nLang = 'en', i18nVersion, legislation, nrvUiVersion, region, renderConfig, renderSettings, renderInSelector, pVendorList, scriptVersionBaseUrl, shouldConsentManagementBeEnforced, vendorListBaseUrl, isEmbeddedVideo, infoSvg, }) => {
    let api = await FrontendAPI.load({
        backendBaseUrl,
        consentString: clientConsentString,
        cvlUiVersion,
        geoInfo: getGeoInfo({ geo, region }),
        gvlUiVersion,
        nrvUiVersion,
        scriptVersionBaseUrl,
        vendorListBaseUrl,
    });
    const geoInfo = getGeoInfo({ geo, region });
    if (await api.callP('hasStorageAccess')) {
        window.addEventListener('message', FrontendAPI.iframeHandler);
    }
    else {
        debug('Cannot use iframed API', DEBUG);
        api.unload();
        api = (await FrontendModAPI.load(backendBaseUrl, clientConsentString, geoInfo, legislation, pVendorList));
        window.addEventListener('message', FrontendModAPI.iframeHandler);
    }
    const [hasChoiceBeenMade, i18n] = await Promise.all([
        api.callP('hasChoiceBeenMade'),
        getI18n(vendorListBaseUrl, i18nVersion, i18nLang),
    ]);
    if (hasChoiceBeenMade) {
        await api.callP('updateCookieIfNeeded');
    }
    const view = app({
        api: api,
        applicableRegulations: getApplicableRegulations(),
        banner,
        cvlUiVersion,
        hasChoiceBeenMade: hasChoiceBeenMade,
        hasTPhrase: i18n.has.bind(i18n),
        renderSettings,
        t: translate(i18n),
        translate: i18n.t.bind(i18n),
        clientConsentString,
        forceSetConsentString,
        nrvUiVersion,
        renderConfig,
        pVendorList,
        vendorListBaseUrl,
        isEmbeddedVideo,
        infoSvg,
        shouldConsentManagementBeEnforced,
    });
    api.view = await view;
    onContentLoaded(async () => {
        let renderIn = renderInSelector && document.querySelector(renderInSelector);
        if (!renderIn) {
            renderIn = document.body;
        }
        // @ts-ignore
        renderIn.appendChild(api.view.raw);
        // Temporary workaround to prevent click working on tooltips on desktop devices
        try {
            if (window &&
                window.PageCriteria &&
                !window.PageCriteria.isMobile &&
                !window.PageCriteria.isTablet) {
                for (const tooltipTrigger of renderIn.querySelectorAll('.mol-ads-cmp--tooltip-trigger')) {
                    tooltipTrigger.addEventListener('click', (event) => {
                        event.preventDefault();
                    });
                }
            }
        }
        catch (error) {
            // Do not break on failure
        }
    });
    return api;
};
const buildBackendAPI = (backendBaseUrl, clientConsentString, geoInfo, legislation, pVendorList) => {
    window.addEventListener('message', BackendAPI.iframeHandler);
    return BackendAPI.load(backendBaseUrl, clientConsentString, geoInfo, legislation, pVendorList);
};
(async () => {
    const options = getOptions();
    if (options.logLevel !== 'off') {
        initDebug({ level: options.logLevel, on: true, api: options.api });
    }
    log('heeellllo dickwad');
    log('script options:', JSON.stringify(options), DEBUG);
    let api;
    let banner;
    if (options.render) {
        banner = defaultBanner;
    }
    else if (options.renderVideo) {
        banner = videoBanner;
    }
    const pVendorList = fetchGVL(options.vendorListBaseUrl, options.gvlUiVersion);
    pVendorList.then((vendorList) => {
        log(`gvl loaded`, vendorList, DEBUG);
    });
    try {
        api =
            options.api === 'frontend'
                ? await buildFrontendAPI({
                    backendBaseUrl: options.backendBaseUrl,
                    banner,
                    cvlUiVersion: options.cvlUiVersion,
                    clientConsentString: options.clientConsentString,
                    geo: options.geo,
                    gvlUiVersion: options.gvlUiVersion,
                    forceSetConsentString: options.forceSetConsentString,
                    i18nLang: options.i18nLang,
                    i18nVersion: options.i18nVersion,
                    legislation: options.legislation,
                    nrvUiVersion: options.nrvUiVersion,
                    region: options.region,
                    renderConfig: options.renderConfig,
                    renderInSelector: options.renderIn,
                    renderSettings: !options.renderVideo,
                    pVendorList,
                    scriptVersionBaseUrl: options.scriptVersionBaseUrl,
                    shouldConsentManagementBeEnforced: options.shouldConsentManagementBeEnforced,
                    vendorListBaseUrl: options.vendorListBaseUrl,
                    isEmbeddedVideo: !!options.renderVideo,
                    infoSvg: options.infoSvg,
                })
                : await buildBackendAPI(options.backendBaseUrl, options.clientconsentstring || '', getGeoInfo({
                    geo: options.geo,
                    region: options.region,
                }), options.legislation, pVendorList);
        api.callQueue();
    }
    catch (error) {
        debug(error, ERROR);
        return;
    }
})();
